import {
  Headphones,
  Truck,
  RefreshCw,
  HardHat,
  Wrench,
  FileCheck,
  ArrowUpRight,
  Phone,
} from "lucide-react";

const SERVICIOS = [
  {
    id: "soporte",
    num: "01",
    name: "Soporte 24/7",
    tagline: "Mesa de ayuda activa",
    description:
      "Mesa de ayuda con cobertura continua, atención multicanal y SLAs medibles. Respondemos cuando tu operación más lo necesita.",
    bullets: [
      "Atención telefónica y remota",
      "SLA por severidad",
      "Reportes de tickets",
    ],
    Icon: Headphones,
  },
  {
    id: "despliegues",
    num: "02",
    name: "Despliegues Masivos",
    tagline: "Roll-outs multi-sucursal",
    description:
      "Coordinamos despliegues a gran escala con cuadrillas certificadas, logística y reporte por sitio. Cientos de puntos, una sola ventana.",
    bullets: [
      "Logística nacional",
      "Cuadrillas certificadas",
      "Reporte por sucursal",
    ],
    Icon: Truck,
  },
  {
    id: "renovacion",
    num: "03",
    name: "Renovación Tecnológica",
    tagline: "Upgrade & migración",
    description:
      "Plan de actualización de parque tecnológico con migración controlada, baja de obsoletos y disposición responsable de equipos.",
    bullets: [
      "Auditoría de parque",
      "Migración por etapas",
      "Disposición responsable",
    ],
    Icon: RefreshCw,
  },
  {
    id: "obras-civiles",
    num: "04",
    name: "Obras Civiles",
    tagline: "Infraestructura física",
    description:
      "Adecuación de sala técnica, canalizaciones, instalación eléctrica y trabajos civiles que habilitan tu equipamiento crítico.",
    bullets: [
      "Canalización & ducterías",
      "Sala técnica",
      "Eléctrico normado",
    ],
    Icon: HardHat,
  },
  {
    id: "implementacion",
    num: "05",
    name: "Implementación & Obras",
    tagline: "Llave en mano",
    description:
      "Desde el diseño técnico hasta la puesta en marcha. Un solo proveedor integra civil, eléctrico, red y equipamiento.",
    bullets: [
      "Diseño técnico",
      "Integración multi-disciplina",
      "Acta de puesta en marcha",
    ],
    Icon: Wrench,
  },
  {
    id: "certificacion-redes",
    num: "06",
    name: "Calificación & Certificación de Redes",
    tagline: "Reporte certificado por punto",
    description:
      "Calificamos y certificamos cada punto de cobre y fibra con equipos calibrados. Entregamos reporte certificado punto por punto.",
    bullets: [
      "Equipos certificadores calibrados",
      "Reporte certificado por punto",
      "Cumplimiento de normas ANSI/TIA",
      "Cobre Cat 6 / 6A · Fibra MM / SM",
    ],
    Icon: FileCheck,
    featured: true,
  },
];

export const Servicios = () => {
  return (
    <section
      id="servicios"
      data-testid="servicios-section"
      className="relative bg-gradient-to-b from-white via-slate-50/60 to-white py-24 md:py-32 overflow-hidden"
    >
      {/* Decorative blobs */}
      <div
        aria-hidden
        className="absolute -top-40 right-1/4 w-[480px] h-[480px] rounded-full bg-[#1FA08A]/[0.06] blur-[140px]"
      />
      <div
        aria-hidden
        className="absolute bottom-20 -left-32 w-[420px] h-[420px] rounded-full bg-[#0A2A4E]/[0.04] blur-[120px]"
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        {/* Section header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end mb-16 md:mb-20">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-5 text-[11px] uppercase tracking-[0.32em] font-mono">
              <span className="text-[#1FA08A] font-semibold">/ 04</span>
              <span className="h-px w-10 bg-slate-300" />
              <span className="text-slate-500 font-semibold">
                Operación · Servicios
              </span>
            </div>
            <h2
              data-testid="servicios-heading"
              className="font-heading text-[#0A2A4E] text-4xl sm:text-5xl md:text-[56px] lg:text-[64px] leading-[1.02] tracking-tighter font-bold"
            >
              Servicios que{" "}
              <span className="relative inline-block">
                <span className="text-[#1FA08A]">sostienen</span>
                <svg
                  aria-hidden
                  viewBox="0 0 280 14"
                  className="absolute -bottom-1 left-0 w-full h-3 text-[#1FA08A]/40"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M2 10 C 60 2, 200 16, 278 6"
                    stroke="currentColor"
                    strokeWidth="3"
                    fill="none"
                    strokeLinecap="round"
                  />
                </svg>
              </span>{" "}
              tu operación.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p
              data-testid="servicios-subtitle"
              className="text-slate-600 text-base md:text-lg leading-relaxed max-w-md lg:ml-auto"
            >
              No solo vendemos equipos. Acompañamos cada etapa de tu
              infraestructura — diseño, obra, despliegue, soporte y
              certificación —{" "}
              <span className="text-[#0A2A4E] font-semibold">
                bajo un solo responsable.
              </span>
            </p>
          </div>
        </div>

        {/* Services grid - 2 cols horizontal cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6">
          {SERVICIOS.map((s) => (
            <ServicioCard key={s.id} servicio={s} />
          ))}
        </div>

        {/* Bottom CTA banner - distinct from Hardware/Soluciones */}
        <div
          data-testid="servicios-cta-banner"
          className="mt-16 md:mt-20 relative overflow-hidden rounded-md border border-[#0A2A4E]/10 bg-gradient-to-br from-[#0A2A4E] to-[#05162a] text-white px-6 md:px-10 py-8 md:py-10"
        >
          {/* Pixel accent */}
          <div
            aria-hidden
            className="absolute top-4 right-4 grid grid-cols-3 gap-1.5 opacity-50"
          >
            {[1, 1, 0, 1, 1, 1, 0, 1, 1].map((v, i) => (
              <span
                key={i}
                className={`w-2 h-2 ${v ? "bg-[#2bc9ae]" : "bg-transparent"}`}
              />
            ))}
          </div>
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex items-start md:items-center gap-5">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-md bg-[#1FA08A] text-white shrink-0 shadow-[0_10px_30px_-10px_rgba(31,160,138,0.7)]">
                <Phone className="w-6 h-6" strokeWidth={1.6} />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.28em] text-[#2bc9ae] font-mono font-semibold mb-1.5">
                  Mesa de ayuda · En línea ahora
                </p>
                <p className="text-white text-lg md:text-2xl font-heading font-semibold tracking-tight">
                  Conecta con un especialista Betec.
                </p>
                <p className="text-slate-300/75 text-sm mt-1">
                  Tiempo de respuesta inicial &lt; 15 minutos · Cobertura 24 / 7 / 365
                </p>
              </div>
            </div>
            <a
              href="#contacto"
              data-testid="servicios-cta"
              className="cta-cotizar group inline-flex items-center gap-2.5 bg-[#1FA08A] hover:bg-[#2bc9ae] text-white px-7 py-3.5 rounded-sm font-semibold text-[14px] tracking-wide transition-all duration-300 hover:-translate-y-0.5 shadow-md hover:shadow-[0_18px_50px_-10px_rgba(43,201,174,0.8)] whitespace-nowrap"
            >
              Hablar con soporte
              <span aria-hidden className="cta-dot" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

const ServicioCard = ({ servicio }) => {
  const { id, num, name, tagline, description, bullets, Icon, featured } =
    servicio;

  return (
    <article
      data-testid={`servicio-card-${id}`}
      className={`group relative bg-white border border-slate-200 rounded-md p-7 md:p-9 overflow-hidden transition-all duration-500 hover:border-[#1FA08A]/60 hover:shadow-[0_28px_60px_-30px_rgba(10,42,78,0.35)] hover:-translate-y-1 ${
        featured ? "md:col-span-2 ring-1 ring-[#1FA08A]/30" : ""
      }`}
    >
      {/* Giant outlined number watermark */}
      <span
        aria-hidden
        className="absolute -top-6 -right-2 md:-right-4 font-heading font-extrabold leading-none select-none pointer-events-none"
        style={{
          fontSize: featured ? "260px" : "200px",
          WebkitTextStroke: "1.5px rgba(31,160,138,0.10)",
          color: "transparent",
          letterSpacing: "-0.04em",
          transition: "all 600ms cubic-bezier(0.22,1,0.36,1)",
        }}
      >
        {num}
      </span>

      {/* Hover accent left bar */}
      <span
        aria-hidden
        className="absolute left-0 top-7 bottom-7 w-[3px] bg-[#1FA08A] scale-y-0 origin-top group-hover:scale-y-100 transition-transform duration-500"
      />

      <div className={`relative ${featured ? "lg:grid lg:grid-cols-12 lg:gap-8 lg:items-start" : ""}`}>
        <div className={featured ? "lg:col-span-7" : ""}>
          {/* Top row: num + tagline */}
          <div className="flex items-center justify-between mb-6">
            <span className="text-[10.5px] font-mono uppercase tracking-[0.28em] font-semibold text-[#1FA08A]">
              / {num}
            </span>
            <span className="text-[10.5px] font-mono uppercase tracking-[0.22em] text-slate-400">
              {tagline}
            </span>
          </div>

          {/* Icon + Title */}
          <div className="flex items-start gap-4 mb-4">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-md bg-[#0A2A4E]/[0.04] text-[#0A2A4E] shrink-0 transition-all duration-500 group-hover:bg-[#1FA08A] group-hover:text-white">
              <Icon className="w-6 h-6" strokeWidth={1.5} />
            </div>
            <h3 className="font-heading text-[#0A2A4E] text-[24px] md:text-[28px] font-semibold tracking-tight leading-[1.1] pt-1">
              {name}
            </h3>
          </div>

          <p className="text-[14.5px] leading-relaxed text-slate-600 mb-6 max-w-xl">
            {description}
          </p>

          {/* CTA */}
          <a
            href="#contacto"
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold uppercase tracking-[0.16em] text-[#0A2A4E] hover:text-[#1FA08A] transition-colors"
          >
            Cotizar este servicio
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>

        {/* Bullets - in right column for featured */}
        <div className={featured ? "mt-6 lg:mt-0 lg:col-span-5 lg:border-l lg:border-slate-200 lg:pl-8" : "mt-2"}>
          {featured && (
            <div className="text-[10.5px] uppercase tracking-[0.22em] font-mono text-[#1FA08A] font-semibold mb-3">
              Incluye
            </div>
          )}
          <ul className={`grid ${featured ? "grid-cols-1 gap-2.5" : "grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5"}`}>
            {bullets.map((b) => (
              <li
                key={b}
                className="flex items-start gap-2.5 text-[13px] text-slate-700 leading-snug"
              >
                <span className="mt-2 w-1 h-1 rounded-full bg-[#1FA08A] shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Bottom accent line */}
      <span
        aria-hidden
        className="absolute left-0 bottom-0 h-[2px] w-0 group-hover:w-full bg-[#1FA08A] transition-all duration-700"
      />
    </article>
  );
};

export default Servicios;
